import React, { useState, useEffect } from 'react';
import { RefreshCw, HelpCircle, Share2, ArrowDown } from 'lucide-react';
import resonanceData from '../data/resonance.json';
import ShareModal from '../components/ShareModal';

const Home = ({ onNavigate }) => {
  const [currentItem, setCurrentItem] = useState(null);
  const [showMeaning, setShowMeaning] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    getRandomWisdom();
  }, []);

  const hasChinese = (text) => /[\u3400-\u9FFF]/.test(text || '');

  const stripOuterQuotes = (text) => {
    if (!text) return '';
    return String(text)
      .trim()
      .replace(/^["“”]+/, '')
      .replace(/["“”]+$/, '')
      .trim();
  };

  const getRandomWisdom = () => {
    const pool = Array.isArray(resonanceData)
      ? resonanceData.filter((item) => item?.east?.text && item?.east?.author)
      : [];

    if (pool.length === 0) return;

    // Ensure the next pick is different from the current one (best-effort)
    let next = pool[Math.floor(Math.random() * pool.length)];
    if (currentItem && pool.length > 1) {
      let tries = 0;
      while (next.id === currentItem.id && tries < 20) {
        next = pool[Math.floor(Math.random() * pool.length)];
        tries += 1;
      }
    }

    setCurrentItem(next);
    setShowMeaning(false);
  };

  const handleShare = () => {
    if (currentItem) {
      setShowShareModal(true);
    }
  };

  if (!currentItem) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cinnabar-red"></div>
      </div>
    );
  };

  const categoryTheme = {
    'Inner Peace & Anxiety': {
      gradient: 'from-dark-slate via-ink-black to-dark-slate',
      blob: 'bg-emerald-400/10',
    },
    'Power & Strategy': {
      gradient: 'from-[#0b1220] via-ink-black to-[#1a0b0e]',
      blob: 'bg-cinnabar-red/10',
    },
    'Relationships & Connection': {
      gradient: 'from-[#0b1b1a] via-ink-black to-dark-slate',
      blob: 'bg-pink-400/10',
    },
    'Destiny & Change': {
      gradient: 'from-[#0a1730] via-ink-black to-[#0b1220]',
      blob: 'bg-sky-400/10',
    },
    'Growth & Mastery': {
      gradient: 'from-[#1a120b] via-ink-black to-[#0b1220]',
      blob: 'bg-amber-400/10',
    },
    'Human Nature': {
      gradient: 'from-[#120b1a] via-ink-black to-dark-slate',
      blob: 'bg-violet-400/10',
    },
    'Wisdom & Life': {
      gradient: 'from-dark-slate via-ink-black to-dark-slate',
      blob: 'bg-porcelain-white/10',
    },
  };

  const theme = categoryTheme[currentItem.category] || categoryTheme['Wisdom & Life'];
  const mainQuote = stripOuterQuotes(currentItem.east.text);
  const secondaryLine = hasChinese(currentItem.east.text)
    ? null
    : (currentItem.east.meaning || null);

  const shareQuote = {
    text: mainQuote,
    chinese: hasChinese(currentItem.east.text) ? currentItem.east.text : undefined,
    author: currentItem.east.author,
    source: currentItem.category || currentItem.theme,
    meaning: currentItem.fusion || currentItem.east.meaning || '',
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-x-hidden">
      {/* Immersive Background */}
      <div className="absolute inset-0 z-0 fixed">
        <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient}`}></div>
        <div className="absolute inset-0 opacity-20">
          <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${theme.blob} rounded-full blur-3xl`}></div>
          <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 ${theme.blob} rounded-full blur-3xl`}></div>
        </div>
        {/* Abstract ink/mountain style pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="none">
            <path d="M0,400 Q300,200 600,400 T1200,400 L1200,800 L0,800 Z" fill="currentColor" />
            <path d="M0,600 Q400,400 800,600 T1200,600 L1200,800 L0,800 Z" fill="currentColor" />
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        {/* Quote Display */}
        <div className="mb-12">
          <blockquote className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-porcelain-white leading-relaxed mb-6">
            "{mainQuote}"
          </blockquote>
          {secondaryLine && (
            <p className="text-base sm:text-lg text-porcelain-white/80 mb-3 line-clamp-2">
              {secondaryLine}
            </p>
          )}
          <p className="text-sm sm:text-base text-porcelain-white/60">
            — {currentItem.east.author}
          </p>
          {currentItem.category && (
            <p className="mt-3 text-xs text-porcelain-white/50 tracking-widest uppercase">
              {currentItem.category}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            onClick={getRandomWisdom}
            className="flex items-center space-x-2 px-6 py-3 bg-cinnabar-red hover:bg-red-700 text-porcelain-white rounded-lg transition-all transform hover:scale-105 shadow-lg"
          >
            <RefreshCw size={20} />
            <span>Get Today's Wisdom</span>
          </button>

          <button
            onClick={() => setShowMeaning(!showMeaning)}
            className="flex items-center space-x-2 px-6 py-3 bg-porcelain-white/10 hover:bg-porcelain-white/20 text-porcelain-white rounded-lg transition-all border border-porcelain-white/20"
          >
            <HelpCircle size={20} />
            <span>What does this mean?</span>
          </button>

          <button
            onClick={handleShare}
            className="flex items-center space-x-2 px-6 py-3 bg-porcelain-white/10 hover:bg-porcelain-white/20 text-porcelain-white rounded-lg transition-all border border-porcelain-white/20 transform hover:scale-105"
          >
            <Share2 size={20} />
            <span>Share</span>
          </button>
        </div>

        {/* Meaning Expansion */}
        {showMeaning && (
          <div className="mt-8 mb-12 p-6 bg-porcelain-white/5 backdrop-blur-sm rounded-lg border border-porcelain-white/10 animate-fadeIn text-left">
            <h3 className="text-xl font-semibold text-porcelain-white mb-3">Meaning</h3>
            <p className="text-porcelain-white/80 leading-relaxed mb-4">
              {currentItem.fusion || currentItem.east.meaning}
            </p>
            {currentItem.theme && (
              <p className="text-sm text-porcelain-white/60">
                Theme: <span className="italic">{currentItem.theme}</span>
              </p>
            )}
          </div>
        )}

        {/* 👇 Explore Our Network (友好链接板块) 👇 */}
        <section className="w-full mt-16 pt-12 border-t border-porcelain-white/10">
          <h3 className="text-xl font-serif text-center text-porcelain-white/90 mb-8 tracking-wide">
            Explore Our Network
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6 text-left">
            {/* 链接 1: Chinese Name */}
            <a 
              href="https://chinesename.shop/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-2xl bg-porcelain-white/5 p-6 border border-porcelain-white/10 hover:border-amber-500/30 transition-all duration-300 hover:-translate-y-1 hover:bg-porcelain-white/10"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <span className="text-xs font-bold tracking-widest text-amber-500 mb-2 block uppercase">
                  Culture & Identity
                </span>
                <h4 className="text-lg sm:text-xl font-serif text-porcelain-white mb-2 group-hover:text-amber-400 transition-colors">
                  Authentic Chinese Names
                </h4>
                <p className="text-porcelain-white/60 text-sm leading-relaxed">
                  Discover your meaningful Chinese name based on poetry and five elements. Connect deeper with Eastern identity.
                </p>
              </div>
            </a>

            {/* 链接 2: Hospitals in China */}
            <a 
              href="https://www.hospitalsinchina.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-2xl bg-porcelain-white/5 p-6 border border-porcelain-white/10 hover:border-emerald-500/30 transition-all duration-300 hover:-translate-y-1 hover:bg-porcelain-white/10"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <span className="text-xs font-bold tracking-widest text-emerald-500 mb-2 block uppercase">
                  Health & Guide
                </span>
                <h4 className="text-lg sm:text-xl font-serif text-porcelain-white mb-2 group-hover:text-emerald-400 transition-colors">
                  Hospitals in China
                </h4>
                <p className="text-porcelain-white/60 text-sm leading-relaxed">
                  The trusted guide for expats seeking medical care in China. Find top-rated international hospitals.
                </p>
              </div>
            </a>
          </div>
        </section>
        {/* 👆 新增板块结束 👆 */}

        {/* Navigation Arrow */}
        <div className="mt-16 animate-bounce">
          <button
            onClick={() => onNavigate('resonance')}
            className="flex flex-col items-center space-y-2 text-porcelain-white/60 hover:text-porcelain-white transition-colors group"
          >
            <span className="text-sm font-medium">Discover Eastern & Western Resonance</span>
            <ArrowDown size={24} className="group-hover:translate-y-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        quote={shareQuote}
      />

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Home;
